import { ALLOWED_UTM_PARAMS } from './constants';
import { getApplicationStep, isApplicationRoute } from './routes';

const ALLOWED_QUERY_PARAMS = new Set<string>(ALLOWED_UTM_PARAMS);
const ALLOWED_HASHES = new Set([
  '#aak',
  '#check',
  '#expose',
  '#faq',
  '#fragen',
  '#gespraechsleitfaden',
  '#kontakt',
  '#mieter',
  '#schadenfall',
  '#team',
  '#vorlagen',
]);

function sanitizeSearch(search: string, hostname?: string) {
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();

  for (const key of ALLOWED_QUERY_PARAMS) {
    const value = params.get(key);

    if (value) {
      next.set(key, value);
    }
  }

  if (hostname === 'youtube.com' || hostname === 'www.youtube.com' || hostname === 'youtu.be') {
    const video = params.get('v');

    if (video && /^[A-Za-z0-9_-]{6,32}$/.test(video)) {
      next.set('v', video);
    }
  }

  const serialized = next.toString();
  return serialized ? `?${serialized}` : '';
}

export function sanitizePathname(pathname: string) {
  if (!isApplicationRoute(pathname)) {
    return pathname || '/';
  }

  const step = getApplicationStep(pathname);

  if (!step || step === 'start') {
    return '/application';
  }

  return `/application/${step}`;
}

export function sanitizeUrl(
  rawUrl: string | undefined,
  fallbackOrigin = typeof window === 'undefined' ? 'https://aareal-aval.de' : window.location.origin
) {
  if (!rawUrl) {
    return rawUrl;
  }

  try {
    const baseUrl = new URL(fallbackOrigin);
    const url = new URL(rawUrl, baseUrl);
    const isApplicationHost =
      url.origin === baseUrl.origin ||
      url.hostname === 'aareal-aval.de' ||
      url.hostname.endsWith('.aareal-aval.de');

    if (isApplicationHost) {
      url.pathname = sanitizePathname(url.pathname);
    }

    url.search = sanitizeSearch(url.search, url.hostname);
    url.hash = '';
    return url.toString();
  } catch {
    return sanitizePathname(rawUrl.split('?')[0] ?? '/');
  }
}

export function sanitizeHref(href: string) {
  if (href.startsWith('mailto:')) {
    return 'mailto:';
  }

  if (href.startsWith('tel:')) {
    return 'tel:';
  }

  const hashIndex = href.indexOf('#');
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const safeHash = ALLOWED_HASHES.has(hash) ? hash : '';

  if (!withoutHash) {
    return safeHash || '#';
  }

  const sanitized = sanitizeUrl(withoutHash) ?? withoutHash;
  return safeHash ? `${sanitized}${safeHash}` : sanitized;
}
